/**
 * Glissement d'un point de passage sur la carte.
 *
 * L'application Android déplace un point en deux temps — un tap pour choisir
 * l'action, un second pour désigner la destination — faute de pouvoir
 * intercepter un glissement sans se battre avec le défilement de la carte.
 * C'est un écart assumé : sur une carte, tirer un point est le geste attendu,
 * et le double tap reste disponible dans le dialogue pour un placement précis
 * ou un doigt peu sûr.
 *
 * MapLibre ne rend pas ses entités déplaçables : on neutralise le défilement
 * le temps du geste et on redessine le point à la main.
 */

import type { Map as MapLibreMap, MapMouseEvent, MapTouchEvent } from 'maplibre-gl'
import type { GpxTrack } from '../core/track'

/**
 * Déplacement en deçà duquel le geste reste un tap.
 *
 * Sans ce seuil, le moindre tremblement pendant un appui transformerait
 * l'ouverture du menu du point en un déplacement d'un pixel.
 */
const DRAG_THRESHOLD_PX = 6

export interface WayViaDragDeps {
  readonly map: MapLibreMap
  /** Traces affichées, pour retrouver celle que le point concerne. */
  readonly tracks: () => readonly GpxTrack[]
  /** Redessine les points de passage pendant le geste, sans rien enregistrer. */
  readonly onPreview: (track: GpxTrack) => void
  /** Position définitive : à enregistrer et à recalculer. */
  readonly onCommit: (
    track: GpxTrack,
    wayId: string,
    viaIndex: number,
    position: { lat: number; lon: number },
  ) => void
  /** Le glissement doit être neutralisé pendant une sélection de point. */
  readonly isBusy: () => boolean
}

interface DragState {
  readonly track: GpxTrack
  readonly wayId: string
  readonly viaIndex: number
  /** Points de passage d'origine, restaurés si le geste est abandonné. */
  readonly original: readonly { lat: number; lon: number }[]
  readonly startX: number
  readonly startY: number
  moved: boolean
}

/** Rend les points de passage déplaçables. Renvoie une fonction de désabonnement. */
export function enableWayViaDrag(deps: WayViaDragDeps): () => void {
  const map = deps.map
  let drag: DragState | null = null

  const viaLayers = (): string[] =>
    map
      .getStyle()
      .layers.map((layer) => layer.id)
      .filter((id) => id.startsWith('way-via-layer-') && map.getLayer(id))

  const begin = (event: MapMouseEvent | MapTouchEvent): void => {
    if (drag !== null || deps.isBusy()) return

    const layers = viaLayers()
    if (layers.length === 0) return

    const [feature] = map.queryRenderedFeatures(event.point, { layers })
    const properties = feature?.properties
    if (!properties) return

    const trackId = properties.track_id
    const wayId = properties.way_id
    const viaIndex = properties.via_index
    if (
      typeof trackId !== 'string' ||
      typeof wayId !== 'string' ||
      typeof viaIndex !== 'number'
    ) {
      return
    }

    const track = deps.tracks().find((candidate) => candidate.id === trackId)
    const way = track?.wayInOuts.find((candidate) => candidate.id === wayId)
    if (!track || !way) return

    // Sans cela, la carte défilerait sous le doigt en même temps que le point.
    event.preventDefault()
    map.dragPan.disable()

    drag = {
      track,
      wayId,
      viaIndex,
      original: way.vias.map((via) => ({ lat: via.lat, lon: via.lon })),
      startX: event.point.x,
      startY: event.point.y,
      moved: false,
    }
  }

  const move = (event: MapMouseEvent | MapTouchEvent): void => {
    if (drag === null) return

    if (!drag.moved) {
      const distance = Math.hypot(
        event.point.x - drag.startX,
        event.point.y - drag.startY,
      )
      if (distance < DRAG_THRESHOLD_PX) return
      drag.moved = true
    }

    apply(drag, event.lngLat.lat, event.lngLat.lng)
    deps.onPreview(drag.track)
  }

  const end = (event: MapMouseEvent | MapTouchEvent): void => {
    const state = drag
    if (state === null) return

    drag = null
    map.dragPan.enable()

    // Geste trop court pour être un déplacement : la position est restaurée et
    // le clic suit son cours, ouvrant le menu du point.
    if (!state.moved) {
      restore(state)
      deps.onPreview(state.track)
      return
    }

    deps.onCommit(state.track, state.wayId, state.viaIndex, {
      lat: event.lngLat.lat,
      lon: event.lngLat.lng,
    })
  }

  const cancel = (): void => {
    if (drag === null) return

    restore(drag)
    deps.onPreview(drag.track)
    drag = null
    map.dragPan.enable()
  }

  map.on('mousedown', begin)
  map.on('touchstart', begin)
  map.on('mousemove', move)
  map.on('touchmove', move)
  map.on('mouseup', end)
  map.on('touchend', end)
  map.on('touchcancel', cancel)

  return () => {
    cancel()
    map.off('mousedown', begin)
    map.off('touchstart', begin)
    map.off('mousemove', move)
    map.off('touchmove', move)
    map.off('mouseup', end)
    map.off('touchend', end)
    map.off('touchcancel', cancel)
  }
}

/** Déplace le point suivi dans le modèle, sans rien enregistrer. */
function apply(state: DragState, lat: number, lon: number): void {
  const way = state.track.wayInOuts.find(
    (candidate) => candidate.id === state.wayId,
  )
  if (!way) return

  const vias = way.vias.map((via, index) =>
    index === state.viaIndex ? { lat, lon } : via,
  )

  state.track.wayInOuts = state.track.wayInOuts.map((candidate) =>
    candidate.id === state.wayId ? { ...candidate, vias } : candidate,
  )
}

/** Remet les points de passage tels qu'ils étaient avant le geste. */
function restore(state: DragState): void {
  state.track.wayInOuts = state.track.wayInOuts.map((candidate) =>
    candidate.id === state.wayId
      ? { ...candidate, vias: state.original.map((via) => ({ ...via })) }
      : candidate,
  )
}
